import { Constants } from "../../model/Constants";
import { log } from "../../chatframe/Utils";
import JanusMessageHandler from "./janusMessageHandler";

async function getJanus() {
    /*
     * Dynamic import see: https://webpack.js.org/api/module-methods/#magic-comments
     * @ts-ignore
     */
    // @ts-ignore
    const janus: any = import(/* webpackChunkName: "janus" */ "./janus");
    return janus;
}

async function getAdapter() {
    // Dynamic import see: https://webpack.js.org/api/module-methods/#magic-comments
    const adapter = import(/* webpackChunkName: "webrtc-adapter" */ "webrtc-adapter");
    return adapter;
}

export default class VideoCallMgr {
    $app: any;

    doSimulcast: any;

    doSimulcast2: any;

    devices: any;

    showCustomerCamera: any;

    sfutest: any;

    mystream: any;

    mypvtid: any;

    server: any;

    roomName: any;

    customerName: any;

    customerJoinChecksum: any;

    opaqueId: string;

    token: any;

    pin: any;

    janusMsgHandler: JanusMessageHandler;

    janus: any;

    constructor(app: any, doSimulcast: any, doSimulcast2: any, devices: any, showCustomerCamera: any) {
        this.$app = app;
        this.doSimulcast = doSimulcast;
        this.doSimulcast2 = doSimulcast2;
        this.devices = devices;
        this.showCustomerCamera = showCustomerCamera;
    }

    /**
     * Closes all the media devices for video element
     * @param {MediaStream} localStream - local stream object
     */
    closeAllMediaDevices(localStream: any): void {
        if (this.sfutest) {
            this.sfutest.send({ message: { request: "leave" } });
            this.sfutest.hangup(true);
            this.sfutest.detach();
            this.janus.destroy();
        }

        const stream = localStream;

        if (stream) {
            // Through the stream, you can get the MediaStreamTracks with getTracks():
            const tracks = stream ? stream.getTracks() : [];

            /**
             *  Tracks are returned as an array, so if you know you only have one,
             *  you can stop it with:
             */
            tracks.forEach((track: any) => track.stop());
        }
    }

    /**
     * Starts call engagement after setting
     * required attributes for video call and connect with
     * janus-gateway server
     * @param {Object} config - config to start and join a webrtc room
     * @param {function} callback - callback to report room status
     */
    startVideoCallEngagement(config: any, callback: any): void {
        try {
            //  local media stream.
            this.mystream = null;

            //  subscriber private id
            this.mypvtid = null;

            //  janus gateway server url/name.
            this.server = config.endpointUrl;

            //  unique numeric ID, optional, chosen by plugin if missing.
            this.roomName = config.roomName;

            //  user name who is connecting the call.
            this.customerName = config.customerName;

            this.customerJoinChecksum = config.customerJoinChecksum;

            const chatParmas = this.$app.getSDKInstance().getChatParams();

            this.opaqueId = JSON.stringify({
                roomName: config.roomName,
                agentID: chatParmas.agentID,
                chatID: chatParmas.chatID,
                customerID: chatParmas.getCustomerID(),
                siteID: chatParmas.siteID,
            });

            //  authentication token to connect with janus gateway server.
            this.token = config.customerToken;

            //  optional password needed for watching the stream.
            this.pin = config.roomPin;
            this.janusMsgHandler = new JanusMessageHandler(this);
            getJanus().then(module => {
                window.Janus = module.default;
                return getAdapter();
            }).then(module => {
                window.adapter = module.default;
                this.joinCall(callback);
            });
        } catch (err) {
            log(`Error: ${err}`);
        }
    }

    /**
     * join video call after echo test success
     */
    joinCall(statusCb: any): void {
        // Create new janus instance
        Janus.init({
            debug: "all",
            dependencies: Janus.useDefaultDependencies({ adapter }),
            callback: () => {
                if (!Janus.isWebrtcSupported()) {
                    statusCb("noSupport");
                    return;
                }

                this.janus = new Janus({
                    server: this.server,
                    token: this.token,
                    success: () => {
                        this.attachToRoom(statusCb);
                    },
                    error: function (error: any): void {
                        Janus.error(error);
                        statusCb(Constants.RTC_ERROR);
                    },
                    destroyed: function () {
                    },
                });
            },
        });
    }

    attachToRoom(statusCb: any): void {
        this.janus.attach(
            {
                plugin: "janus.plugin.videoroom",
                opaqueId: this.opaqueId,
                success: (pluginHandle: any): void => {
                    this.sfutest = pluginHandle;
                    Janus.log(`Plugin attached! (${this.sfutest.getPlugin()},
                                     id=${this.sfutest.getId()})`);
                    Janus.log("  -- This is a publisher/manager");

                    // Prepare the username registration
                    const register = {
                        request: "join",
                        room: this.roomName,
                        ptype: "publisher",
                        display: this.customerName,
                        pin: this.pin || "",
                        id: this.customerName,
                        join_checksum: this.customerJoinChecksum,
                    };
                    this.sfutest.send({ message: register });
                },
                error: (error: any): void => {
                    Janus.error("  -- Error attaching plugin...", error);
                    statusCb(Constants.RTC_ERROR);
                },
                iceState: (state: any): void => {
                    Janus.log(`ICE state changed to ${state}`);
                },
                mediaState: (medium: any, on: any): void => {
                    Janus.log(`Janus ${on ? "started" : "stopped"} 
                                receiving our ${medium}`);
                },
                webrtcState: (on: any): void => {
                    Janus.log(`Janus says our WebRTC PeerConnection 
                                is ${on ? "up" : "down"} now`);
                },
                onlocalstream: (stream: any): void => {
                    Janus.debug(" ::: Got a local stream :::", stream);
                    statusCb(Constants.CUSTOMER_VIDEO_PUBISHED, stream);
                },
                onmessage: (msg: any, jsep: any): void => {
                    Janus.debug(" ::: Got a message (publisher) :::", msg);
                    const event = msg["videoroom"];
                    Janus.debug(`Event: ${event}`);

                    this.janusMsgHandler.handleServerMessage(event, msg, statusCb);

                    if (jsep) {
                        Janus.debug("Handling SDP as well...", jsep);
                        this.sfutest.handleRemoteJsep({ jsep: jsep });

                        /*
                         * Check if any of the media we wanted to publish has
                         * been rejected (e.g., wrong or unsupported codec)
                         */
                        const audio = msg["audio_codec"];

                        if (this.mystream && this.mystream.getAudioTracks()
                            && mystream.getAudioTracks().length > 0 && !audio
                        ) {
                            // Audio has been rejected
                            Janus.log("Our audio stream has been rejected,"
                                + " viewers won't hear us");
                            statusCb(Constants.LOCAL_AUDIO_ERROR);
                        }

                        const video = msg["video_codec"];

                        if (this.mystream && this.mystream.getVideoTracks()
                            && mystream.getVideoTracks().length > 0 && !video
                        ) {
                            statusCb(Constants.LOCAL_VIDEO_ERROR);
                        }
                    }
                },
                onremotestream: function (stream: any): void {
                    log(`stream : ${stream}`);

                    /*
                     * The publisher stream is sendonly,
                     * we don't expect anything here
                     */
                },
                oncleanup: () => {
                    Janus.log(` ::: Got a cleanup notification: 
                            we are unpublished now :::`);
                    this.mystream = null;
                },
            }
        );
    }

    /**
     * publish local media stream
     * @param {AudioContext} useAudio - audio context of stream
     * @param {function} statusCb - call back to provide status update
     */
    publishOwnFeed(useAudio: any, statusCb: any): void {
        // Publish our stream
        this.createLocalOffer(this.getMedia(useAudio, this.devices,
            this.showCustomerCamera), statusCb);
    }

    replaceOwnFeed(deviceSettings: any, useAudio: any, statusCb: any): void {
        const media: any = this.getMedia(useAudio, deviceSettings, this.showCustomerCamera);

        if (deviceSettings.audio) {
            media.replaceAudio = true;
        }

        if (deviceSettings.video) {
            media.replaceVideo = true;
        }

        this.createLocalOffer(media, useAudio, statusCb);
    }

    getMedia(useAudio: any, devices: any, showCustomerCamera: any): any {
        const media: any = {
            audioRecv: false,
            videoRecv: false,
            audioSend: useAudio,
            videoSend: true,
        };

        if (devices.audio) {
            media.audio = devices.audio;
        }

        if (devices.video) {
            media.video = devices.video;
        }

        if (!showCustomerCamera) {
            media.videoSend = false;
        }
        return media;
    }

    createLocalOffer(media: any, useAudio: any, statusCb?: any): void {
        // Publish our stream
        this.sfutest.createOffer(
            {

                // Add data:true here if you want to publish datachannels as well
                media: media,

                // Publishers are sendonly
                simulcast: this.doSimulcast,
                simulcast2: this.doSimulcast2,
                success: (jsep: any) => {
                    Janus.debug("Got publisher SDP!", jsep);
                    const publish = {
                        request: "configure",
                        audio: useAudio,
                        video: media.videoSend,
                    };
                    this.sfutest.send({
                        message: publish,
                        jsep: jsep,
                    });
                },
                error: (error: any): void => {
                    Janus.error("WebRTC error:", error);
                    statusCb(Constants.LOCAL_AUDIO_ERROR);
                },
            }
        );
    }

    newRemoteFeed(id: any, display: any, audio: any, video: any, statusCb: any): void {
        /**
         *A new feed has been published,
         *create a new plugin handle and attach to it as a subscriber
         */

        log("Someone has joined! Now its time to change styles");
        let remoteFeed: any = null;
        this.janus.attach(
            {
                plugin: "janus.plugin.videoroom",
                opaqueId: this.opaqueId,
                success: (pluginHandle: any): void => {
                    remoteFeed = pluginHandle;
                    remoteFeed.simulcastStarted = false;
                    Janus.log(`Plugin attached! (${remoteFeed.getPlugin()}, id=${remoteFeed.getId()})`);
                    Janus.log("  -- This is a subscriber");

                    // Wait for the plugin to send us an offer
                    const subscribe = {
                        request: "join",
                        room: this.roomName,
                        ptype: "subscriber",
                        feed: id,
                        pin: this.pin || "",
                        private_id: this.mypvtid,
                    };
                    remoteFeed.videoCodec = video;
                    remoteFeed.send({ message: subscribe });
                },
                error: (error: any) => {
                    Janus.error("  -- Error attaching plugin...", error);
                    statusCb(Constants.RTC_ERROR);
                },
                onmessage: (msg: any, jsep: any): void => {
                    Janus.debug(" ::: Got a message (subscriber) :::", msg);
                    const event = msg["videoroom"];
                    Janus.debug(`Event: ${event}`);

                    this.janusMsgHandler.handleServerMessageForRemoteFeed(msg, event, remoteFeed, statusCb);

                    if (jsep) {
                        Janus.debug("Handling SDP as well...", jsep);

                        // Answer and attach
                        remoteFeed.createAnswer(
                            {
                                jsep: jsep,

                                /*
                                 * Add data:true here if you want to subscribe to datachannels as well
                                 * (obviously only works if the publisher offered them in the
                                 * first place)
                                 */
                                // We want recvonly audio/video
                                media: { audioSend: false, videoSend: false },
                                success: (jsep: any): void => {
                                    Janus.debug("Got SDP!", jsep);
                                    const body = { request: "start", room: this.roomName };
                                    remoteFeed.send({ message: body, jsep: jsep });
                                },
                                error: (error: any): void => {
                                    Janus.error("WebRTC error:", error);
                                    statusCb(Constants.RTC_ERROR);
                                },
                            }
                        );
                    }
                },
                iceState: (state: any): void => {
                    Janus.log(`ICE state of this WebRTC PeerConnection (feed #${remoteFeed.rfindex})
                 changed to ${state}`);
                },
                webrtcState: (on: any): void => {
                    Janus.log(`Janus says this WebRTC PeerConnection (feed #${remoteFeed.rfindex})
                 is ${on ? "up" : "down"} now`);
                },
                onlocalstream: (stream: any) => {
                    // The subscriber stream is recvonly, we don't expect anything here
                },
                onremotestream: (stream: any) => {
                    Janus.debug(`Remote feed #${remoteFeed.rfindex}, stream:`, stream);
                    statusCb(Constants.AGENT_VIDEO_PUBISHED, stream);
                },
                oncleanup: () => {
                    Janus.log(` ::: Got a cleanup notification (remote feed ${id}) :::`);
                },
            }
        );
    }

    attachStream(videoEl: any, stream: any): void {
        Janus.attachMediaStream(videoEl, stream);
    }
}
